import {
  DestroyRef,
  Directive,
  ElementRef,
  Renderer2,
  effect,
  inject,
  signal,
} from '@angular/core';
import { injectExposesStateProvider } from '@spartan-ng/brain/core';
import { classes } from '@spartan-ng/helm/utils';

/** Space to keep between the popover panel and the viewport edge, in pixels. */
const VIEWPORT_MARGIN = 8;

@Directive({
  selector: '[hlmPopoverContent],hlm-popover-content',
  host: { 'data-slot': 'popover-content', class: 'ocp-spartan-scope' },
})
export class HlmPopoverContent {
  private readonly _stateProvider = injectExposesStateProvider({ host: true });
  public state = this._stateProvider.state ?? signal('closed');
  private readonly _renderer = inject(Renderer2);
  private readonly _element = inject(ElementRef);
  private readonly _destroyRef = inject(DestroyRef);

  private _cleanupClamp?: () => void;

  constructor() {
    effect(() => {
      const state = this.state();
      this._renderer.setAttribute(this._element.nativeElement, 'data-state', state);

      if (state === 'open') {
        this._watchViewportClamp();
      } else {
        this._cleanupClamp?.();
      }
    });

    this._destroyRef.onDestroy(() => this._cleanupClamp?.());

    classes(
      () =>
        'bg-popover-background text-popover-text data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 ring-popover-border gap-2.5 rounded-lg p-2.5 text-sm shadow-popover ring-1 duration-100 relative flex flex-col outline-none'
    );
  }

  // BrnPopover positions the overlay with `withPush(false)`, so it never gets nudged back inside
  // the viewport on its own. Keep it visible by clamping the CDK pane's inline position whenever
  // it's (re)placed, while leaving it anchored as close to the trigger as the viewport allows.
  private _watchViewportClamp(): void {
    this._cleanupClamp?.();

    const pane = this._element.nativeElement.closest('.cdk-overlay-pane') as HTMLElement | null;
    if (!pane) {
      return;
    }

    const clamp = () => this._clampToViewport(pane);
    // rAF lets CDK finish positioning the pane for this frame before we read/adjust its rect.
    const scheduleClamp = () => requestAnimationFrame(clamp);

    scheduleClamp();
    const observer = new MutationObserver(scheduleClamp);
    observer.observe(pane, { attributes: true, attributeFilter: ['style'] });
    window.addEventListener('resize', scheduleClamp);

    this._cleanupClamp = () => {
      observer.disconnect();
      window.removeEventListener('resize', scheduleClamp);
      this._cleanupClamp = undefined;
    };
  }

  private _clampToViewport(pane: HTMLElement): void {
    const rect = pane.getBoundingClientRect();

    // Clamping (not independent left/right overflow checks) keeps this idempotent - re-running it
    // on an already-clamped rect always yields the same position, so it can't oscillate between
    // frames. When the pane is wider/taller than the viewport, both bounds collapse to the margin,
    // pinning it to a single edge instead of alternating between the two whiplashing MutationObserver.
    const minLeft = VIEWPORT_MARGIN;
    const maxLeft = Math.max(minLeft, window.innerWidth - VIEWPORT_MARGIN - rect.width);
    const clampedLeft = Math.min(Math.max(rect.left, minLeft), maxLeft);
    if (clampedLeft !== rect.left) {
      pane.style.left = `${clampedLeft}px`;
    }

    const minTop = VIEWPORT_MARGIN;
    const maxTop = Math.max(minTop, window.innerHeight - VIEWPORT_MARGIN - rect.height);
    const clampedTop = Math.min(Math.max(rect.top, minTop), maxTop);
    if (clampedTop !== rect.top) {
      pane.style.top = `${clampedTop}px`;
    }
  }
}
